import { FC } from "react";
import { useTranslation } from "react-i18next";

import { NO_VALUE_DASH, useFormats } from "@ryde/shared";

import { IPlaybackModel } from "../investigation.models";
import {
  DelayChipStyled,
  PanelSectionStyled,
  PanelSectionTitleStyled,
  StatGridStyled,
} from "../investigation.panel.styled";
import {
  delayColor,
  formatDurationSec,
  formatKm,
  formatKmh,
  formatSignedMinutes,
} from "../investigation.utils";
import { InvestigationDefinitionRow } from "./investigation.definition-row";
import { InvestigationStatTile } from "./investigation.stat-tile";

/** Ride summary: identity, scheduled-vs-actual timing, and headline metrics. */
export const InvestigationSummary: FC<{ model: IPlaybackModel }> = ({
  model,
}) => {
  const { t } = useTranslation();
  const { formatDateTimeNormalized } = useFormats();
  const summary = model.payload.summary;
  const label = (key: string): string =>
    t(`rideMap.investigation.summary.${key}`);

  return (
    <>
      <PanelSectionStyled>
        <InvestigationDefinitionRow
          label={label("status")}
          value={summary.status}
        />
        {summary.direction && (
          <InvestigationDefinitionRow
            label={label("direction")}
            value={summary.direction}
          />
        )}
        {summary.driverName && (
          <InvestigationDefinitionRow
            label={label("driver")}
            value={summary.driverName}
          />
        )}
        {summary.vehiclePlate && (
          <InvestigationDefinitionRow
            label={label("vehicle")}
            value={summary.vehiclePlate}
          />
        )}
        {summary.supplierName && (
          <InvestigationDefinitionRow
            label={label("supplier")}
            value={summary.supplierName}
          />
        )}
      </PanelSectionStyled>

      <PanelSectionStyled>
        <InvestigationDefinitionRow
          label={label("scheduledStart")}
          value={formatDateTimeNormalized(summary.scheduledStart)}
        />
        <InvestigationDefinitionRow
          label={label("actualStart")}
          value={formatDateTimeNormalized(summary.actualStart)}
        />
        <InvestigationDefinitionRow
          label={label("scheduledEnd")}
          value={formatDateTimeNormalized(summary.scheduledEnd)}
        />
        <InvestigationDefinitionRow
          label={label("actualEnd")}
          value={formatDateTimeNormalized(summary.actualEnd)}
        />
        <InvestigationDefinitionRow
          label={label("delay")}
          value={
            <DelayChipStyled $color={delayColor(summary.delayMinutes)}>
              {formatSignedMinutes(summary.delayMinutes)}
            </DelayChipStyled>
          }
        />
      </PanelSectionStyled>

      <PanelSectionStyled>
        <PanelSectionTitleStyled>
          {t("rideMap.investigation.tabs.summary")}
        </PanelSectionTitleStyled>
        <StatGridStyled>
          <InvestigationStatTile
            label={label("plannedDistance")}
            value={formatKm(summary.plannedDistanceKm)}
          />
          <InvestigationStatTile
            label={label("actualDistance")}
            value={formatKm(summary.actualDistanceKm)}
          />
          <InvestigationStatTile
            label={label("plannedDuration")}
            value={formatDurationSec(summary.plannedDurationSec)}
          />
          <InvestigationStatTile
            label={label("actualDuration")}
            value={formatDurationSec(summary.actualDurationSec)}
          />
          <InvestigationStatTile
            label={label("avgSpeed")}
            value={formatKmh(summary.avgSpeedKmh)}
          />
          <InvestigationStatTile
            label={label("detour")}
            value={summary.hadDetour ? label("hadDetour") : label("noDetour")}
          />
          <InvestigationStatTile
            label={label("passengers")}
            value={
              summary.seatsCount ?? summary.passengersCount ?? NO_VALUE_DASH
            }
          />
          <InvestigationStatTile
            label={label("stations")}
            value={summary.stationsCount}
          />
          <InvestigationStatTile
            label={label("boarded")}
            value={summary.boardedCount}
          />
          <InvestigationStatTile
            label={label("noShow")}
            value={summary.noShowCount}
          />
        </StatGridStyled>
      </PanelSectionStyled>
    </>
  );
};
